import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { mattersApi, matterKeys } from '../api';
import type { MatterQaScore, QaCriterion } from '../types';

/**
 * Matter QA scoring — the input K1 (Quality_Index) is computed from.
 *
 * TS Lot 1 §5.2 defines QA_Score as a weighted **reviewer** assessment against
 * the criteria matrix on a 1.0–5.0 scale; §6.3 turns it into
 * `K1 = QA_Score × (1 − Total_Penalty) + EXC_Bonus`. Until now the backend
 * (`MatterQaScore`, `POST /matters/:id/qa`) existed with no screen to enter a
 * score, so K1 was empty everywhere — the gap the 2026-07-26 review recorded as
 * "K1 is not calculated anywhere".
 *
 * Distinct from `CaseK3RatingDialog`: that captures K3, a single overall manager
 * rating. This captures K1, a per-criterion quality assessment. Both may be
 * given on the same closed matter, by design — they are different components of
 * the §10.1 formula.
 *
 * The server owns the rules (closed matter, rater membership, seniority matrix,
 * no self-rating); this dialog mirrors them only to keep the UI honest.
 */

const SCORES = [1, 2, 3, 4, 5];

function scoreButtonClass(score: number, selected: number | null): string {
  const base =
    'h-8 w-8 rounded-full border-2 text-xs font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ';
  const isSelected = selected === score;
  if (score === 5)
    return base + (isSelected ? 'bg-green-500 border-green-500 text-white scale-110 shadow' : 'border-green-300 text-green-700 hover:bg-green-100');
  if (score === 4)
    return base + (isSelected ? 'bg-emerald-400 border-emerald-400 text-white scale-110 shadow' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-100');
  if (score === 3)
    return base + (isSelected ? 'bg-amber-400 border-amber-400 text-white scale-110 shadow' : 'border-amber-300 text-amber-700 hover:bg-amber-100');
  if (score === 2)
    return base + (isSelected ? 'bg-orange-500 border-orange-500 text-white scale-110 shadow' : 'border-orange-300 text-orange-700 hover:bg-orange-100');
  return base + (isSelected ? 'bg-red-500 border-red-500 text-white scale-110 shadow' : 'border-red-300 text-red-700 hover:bg-red-100');
}

/** Unwrap either a paginated `{ results }` envelope or a bare array. */
function toCriteriaList(
  data: { results?: QaCriterion[] } | QaCriterion[] | undefined,
): QaCriterion[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data?.results ?? [];
}

interface MatterQaScoringDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: number | undefined;
  /** The team member being assessed. */
  person: { id: number; name: string } | null | undefined;
  /** Id of the signed-in rater — used to pre-fill their own earlier scores. */
  raterId: number | undefined;
  onSuccess?: () => void;
}

export function MatterQaScoringDialog({
  open,
  onOpenChange,
  matterId,
  person,
  raterId,
  onSuccess,
}: MatterQaScoringDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [scores, setScores] = React.useState<Record<number, number>>({});
  const [comment, setComment] = React.useState('');

  const { data: criteriaData, isLoading: criteriaLoading } = useQuery({
    queryKey: matterKeys.qaCriteria(),
    queryFn: () => mattersApi.getQaCriteria(),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const { data: existing, isLoading: existingLoading } = useQuery({
    queryKey: matterKeys.qa(matterId ?? ''),
    queryFn: () => mattersApi.getQa(matterId!),
    enabled: open && !!matterId,
  });

  const criteria = React.useMemo(
    () => toCriteriaList(criteriaData),
    [criteriaData],
  );

  // Pre-fill with this rater's own earlier scores for this person, so the
  // dialog edits rather than silently re-submits (the API upserts on the
  // matter+rated+rater+criterion key).
  React.useEffect(() => {
    if (!open) return;
    const mine = (existing ?? []).filter(
      (s: MatterQaScore) =>
        s?.ratedUserId === person?.id && s?.raterUserId === raterId,
    );
    setScores(
      Object.fromEntries(mine.map((s) => [s.criteriaId, s.score])) as Record<
        number,
        number
      >,
    );
    setComment(mine.find((s) => s?.comment)?.comment ?? '');
  }, [open, existing, person?.id, raterId]);

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!matterId || !person) return;
      await mattersApi.submitQa(matterId, {
        scores: Object.entries(scores).map(([criteriaId, score]) => ({
          ratedUserId: person.id,
          criteriaId: Number(criteriaId),
          score,
          ...(comment ? { comment } : {}),
        })),
      });
    },
    onSuccess: () => {
      toast.success(t('matterQa.saved'));
      if (matterId) {
        void queryClient.invalidateQueries({ queryKey: matterKeys.qa(matterId) });
        void queryClient.invalidateQueries({ queryKey: matterKeys.team(matterId) });
      }
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const isLoading = criteriaLoading || existingLoading;
  const scoredCount = Object.keys(scores).length;

  // Live weighted preview of QA_Score, so the reviewer sees the number their
  // input produces before saving (display rule D5).
  const preview = React.useMemo(() => {
    const scored = criteria.filter((c) => scores[c.id] != null);
    if (!scored.length) return null;
    const weight = scored.reduce((s, c) => s + (c.weight || 1), 0);
    const weighted = scored.reduce(
      (s, c) => s + scores[c.id] * (c.weight || 1),
      0,
    );
    return weight > 0 ? weighted / weight : null;
  }, [criteria, scores]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('matterQa.dialogTitle')}</DialogTitle>
          <DialogDescription className="mt-0.5 font-medium text-slate-700">
            {person?.name}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2 py-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : criteria.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-500">
            {t('matterQa.noCriteria')}
          </p>
        ) : (
          <div className="space-y-1">
            {criteria.map((c) => (
              <div
                key={c.id}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 py-2 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-800">
                    {c.name}
                  </p>
                  <p className="text-[11px] text-slate-400">
                    {t('matterQa.weight', {
                      weight: Math.round((c.weight || 0) * 100),
                    })}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {SCORES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      aria-pressed={scores[c.id] === s}
                      className={scoreButtonClass(s, scores[c.id] ?? null)}
                      onClick={() =>
                        setScores((prev) => ({ ...prev, [c.id]: s }))
                      }
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-1.5">
          <label className="text-sm text-slate-600">
            {t('comment')}{' '}
            <span className="text-slate-400">({t('optional')})</span>
          </label>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            className="resize-none"
            placeholder={t('matterQa.commentPlaceholder')}
          />
        </div>

        {/* What this input produces — QA_Score and its K1 normalisation. */}
        <div className="space-y-0.5 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
          <p className="mb-1 font-semibold text-slate-700">
            {t('matterQa.formulaTitle')}
          </p>
          <p className="font-mono">K1 = QA_Score × (1 − Total_Penalty) + EXC_Bonus</p>
          <p className="font-mono">K1_norm = min(1, K1 / 5)</p>
          <p
            className={cn(
              'mt-1 font-medium',
              preview != null ? 'text-slate-800' : 'text-slate-400',
            )}
          >
            {preview != null
              ? t('matterQa.preview', {
                  score: preview.toFixed(2),
                  scored: scoredCount,
                  total: criteria.length,
                })
              : t('matterQa.previewEmpty')}
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitMutation.isPending}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={() => submitMutation.mutate()}
            disabled={scoredCount === 0 || submitMutation.isPending}
          >
            {submitMutation.isPending ? t('processing') : t('save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
